import { Configuration, LogLevel, PublicClientApplication } from "@azure/msal-browser";

/**
 * MSAL Configuration for Azure AD B2C
 *
 * This configuration supports:
 * - Azure AD B2C multi-policy flows (signin, resetpassword, newtenant)
 * - Token caching in localStorage/sessionStorage
 * - Comprehensive logging for debugging
 * - MSAL Browser v4 async initialization pattern
 */

export interface B2CConfig {
  instance: string;
  tenant: string;
  clientId: string;
  cacheLocation: "localStorage" | "sessionStorage";
  scopes: string[];
  policies: {
    signin: string;
    resetpassword: string;
    newtenant: string;
  };
  redirectUri: string;
  postLogoutRedirectUri: string;
}

/**
 * Default scopes required for all authentication flows
 */
const defaultScopes = ["openid"];

/**
 * Creates MSAL configuration from B2C settings
 * @param config B2C configuration object
 * @returns MSAL Configuration object
 */
export function createMsalConfig(config: B2CConfig): Configuration {
  // Merge custom scopes with default scopes (deduplicated)
  const loginScopes = Array.from(new Set([...config.scopes, ...defaultScopes]));

  return {
    auth: {
      authority: config.instance,
      clientId: config.clientId,
      redirectUri: config.redirectUri,
      knownAuthorities: Object.values(config.policies).map((policy) => {
        return config.instance.replace(config.policies.signin, policy);
      }),
      navigateToLoginRequestUrl: false,
      postLogoutRedirectUri: config.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: config.cacheLocation,
      storeAuthStateInCookie: true, // Store auth state in cookie to prevent cache errors
      secureCookies: window.location.protocol === "https:",
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              return;
            case LogLevel.Info:
              console.info(message);
              return;
            case LogLevel.Verbose:
              console.debug(message);
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
          }
        },
        logLevel: LogLevel.Warning,
      },
    },
  };
}

/**
 * Initialize B2C configuration from environment variables
 */
export function initializeB2CConfig(): B2CConfig {
  return {
    instance: import.meta.env.VITE_B2C_CONFIG_INSTANCE,
    tenant: import.meta.env.VITE_B2C_CONFIG_TENANT,
    clientId: import.meta.env.VITE_B2C_CONFIG_CLIENT_ID,
    cacheLocation: import.meta.env.VITE_B2C_CONFIG_CACHE_LOCATION as
      | "localStorage"
      | "sessionStorage",
    scopes: JSON.parse(import.meta.env.VITE_B2C_CONFIG_SCOPES || "[]"),
    policies: JSON.parse(import.meta.env.VITE_B2C_CONFIG_POLICIES || "{}"),
    redirectUri: window.location.origin + "/auth/callback",
    postLogoutRedirectUri: window.location.href,
  };
}

/**
 * Create and initialize MSAL instance
 *
 * IMPORTANT: MSAL Browser v4 requires async initialization
 * This must be called before any authentication operations
 *
 * @returns Initialized PublicClientApplication instance
 */
export async function createMsalInstance(): Promise<PublicClientApplication> {
  const b2cConfig = initializeB2CConfig();
  const msalConfig = createMsalConfig(b2cConfig);

  const msalInstance = new PublicClientApplication(msalConfig);

  // MSAL v4 Breaking Change: initialize() must be called and awaited
  await msalInstance.initialize();

  // Handle redirect promise to complete auth flow
  // This is CRITICAL to prevent no_token_request_cache_error
  try {
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      console.log("Successfully handled redirect response");
      // Rehydrate the post-login redirect target from the OAuth `state` parameter set by
      // LoginHandler (via authService.login) before loginRedirect. This is the primary
      // channel that survives sessionStorage being cleared during the redirect round-trip.
      // We restore it into sessionStorage so callback.tsx reads from a single source.
      if (typeof response.state === "string" && response.state.startsWith("/")) {
        sessionStorage.setItem("redirectAfterLogin", response.state);
      }
    }
  } catch (error) {
    console.error("Error handling redirect:", error);
    // Don't throw - let the app handle auth errors gracefully
  }

  return msalInstance;
}

/**
 * Get login scopes (including default scopes)
 */
export function getLoginScopes(b2cConfig: B2CConfig): string[] {
  return Array.from(new Set([...b2cConfig.scopes, ...defaultScopes]));
}

/**
 * Export singleton B2C config for use across the app
 */
export const b2cConfig = initializeB2CConfig();

/**
 * Export policies for B2C flows (signin, resetpassword, newtenant)
 * Used by useB2CPolicies hook for policy-specific redirect handling
 */
export const policies = b2cConfig.policies;

/**
 * Export login scopes for use in authentication flows
 */
export const loginScopes = getLoginScopes(b2cConfig);

/**
 * Login request configuration for redirect flows
 * Used by ProtectedRoute and other authentication flows
 */
export const loginRequest = {
  scopes: loginScopes,
};
